// Utility functions
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    const facetForm = this.querySelector("form");
    if (!facetForm) return;
    
    // All selects and inputs trigger auto-submit with debounce for text inputs
    const selects = facetForm.querySelectorAll('select');
    selects.forEach(select => {
      select.addEventListener("change", (event) => {
        this.onSubmitHandler(event);
      });
    });

    // Price range inputs with debounce
    const priceInputs = facetForm.querySelectorAll('.price-range__input');
    const debouncedSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);
    
    priceInputs.forEach(input => {
      input.addEventListener("input", debouncedSubmit);
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById("ProductCount");
    const countContainerDesktop = document.getElementById("ProductCountDesktop");
    
    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner"
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove("hidden"));
    
    const productGrid = document.getElementById("ProductGridContainer");
    if (productGrid) {
      const collectionGrid = productGrid.querySelector(".collection__product-grid, .collection, #product-grid");
      if (collectionGrid) {
        collectionGrid.classList.add("loading");
      }
    }
    
    if (countContainer) countContainer.classList.add("loading");
    if (countContainerDesktop) countContainerDesktop.classList.add("loading");

    // Check if this is a mobile form submission
    const isMobileSubmission = event && (
      event.mobileFormSubmitted ||
      (event.target && event.target.closest('#FacetFiltersFormMobile'))
    );
    const mobileDrawer = document.querySelector('menu-drawer.mobile-facets');

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    // Close mobile drawer after applying filters
    if (isMobileSubmission && mobileDrawer && typeof mobileDrawer.closeDrawer === 'function') {
      setTimeout(() => {
        mobileDrawer.closeDrawer();
      }, 500);
    }

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        
        // Re-initialize mobile drawer after update
        const mobileDrawer = document.querySelector('menu-drawer.mobile-facets');
        if (mobileDrawer && typeof mobileDrawer.bindEvents === 'function') {
          mobileDrawer.bindEvents();
        }
        
        if (typeof initializeScrollAnimationTrigger === "function")
          initializeScrollAnimationTrigger(html.innerHTML);
      })
      .catch((error) => {
        console.error('FacetFiltersForm: Error fetching section:', error);
        const loadingSpinners = document.querySelectorAll(
          ".facets-container .loading__spinner, facet-filters-form .loading__spinner"
        );
        loadingSpinners.forEach((spinner) => spinner.classList.add("hidden"));
        
        const productGrid = document.getElementById("ProductGridContainer");
        if (productGrid) {
          const collectionGrid = productGrid.querySelector(".collection__product-grid, .collection, #product-grid");
          if (collectionGrid) {
            collectionGrid.classList.remove("loading");
          }
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    
    const mobileDrawer = document.querySelector('menu-drawer.mobile-facets');
    if (mobileDrawer && typeof mobileDrawer.bindEvents === 'function') {
      mobileDrawer.bindEvents();
    }
    
    if (typeof initializeScrollAnimationTrigger === "function")
      initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    const container = document.getElementById("ProductGridContainer");
    if (!container) {
      console.error("FacetFiltersForm: ProductGridContainer not found");
      return;
    }
    
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const newContainer = parsedHTML.getElementById("ProductGridContainer");
    if (newContainer) {
      container.innerHTML = newContainer.innerHTML;
    }

    container.querySelectorAll(".scroll-trigger").forEach((element) => {
      element.classList.add("scroll-trigger--cancel");
    });
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const countElement = parsedHTML.getElementById("ProductCount");
    if (!countElement) return;
    
    const count = countElement.innerHTML;
    const container = document.getElementById("ProductCount");
    const containerDesktop = document.getElementById("ProductCountDesktop");
    
    if (container) {
      container.innerHTML = count;
      container.classList.remove("loading");
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove("loading");
    }
    
    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner"
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add("hidden"));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    
    // Update all facet filter forms
    const formIds = ['FacetFiltersForm', 'FacetFiltersFormSidebar', 'FacetFiltersFormMobile'];
    
    formIds.forEach(formId => {
      const sourceForm = parsedHTML.getElementById(formId);
      const targetForm = document.getElementById(formId);
      
      if (sourceForm && targetForm) {
        const facetFiltersForm = targetForm.closest('facet-filters-form');
        
        // Update the form wrapper content
        const sourceWrapper = sourceForm.querySelector('#FacetsWrapperDesktop');
        const targetWrapper = targetForm.querySelector('#FacetsWrapperDesktop');
        
        if (sourceWrapper && targetWrapper) {
          targetWrapper.innerHTML = sourceWrapper.innerHTML;
          
          // Re-bind select listeners
          if (facetFiltersForm) {
            const selects = targetWrapper.querySelectorAll('select');
            selects.forEach(select => {
              select.addEventListener("change", (e) => {
                facetFiltersForm.onSubmitHandler(e);
              });
            });
            
            // Re-bind price input listeners with debounce
            const priceInputs = targetWrapper.querySelectorAll('.price-range__input');
            const debouncedSubmit = debounce((e) => {
              facetFiltersForm.onSubmitHandler(e);
            }, 800);
            
            priceInputs.forEach(input => {
              input.addEventListener("input", debouncedSubmit);
            });
          }
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      ".active-facets-mobile",
      ".active-facets-desktop",
    ];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const targetElement = document.querySelector(selector);
      if (targetElement) {
        targetElement.innerHTML = activeFacetsElement.innerHTML;
      }
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  }

  static getSections() {
    const productGrid = document.getElementById("product-grid");
    if (!productGrid || !productGrid.dataset.id) {
      console.error("FacetFiltersForm: product-grid element or data-id not found");
      return [];
    }
    return [
      {
        section: productGrid.dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    if (event && event.target) {
      const mobileForm = event.target.closest('#FacetFiltersFormMobile');
      if (mobileForm && !event.mobileFormSubmitted) {
        event.mobileFormSubmitted = true;
      }
    }
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    
    const sortFilterForms = document.querySelectorAll("facet-filters-form form");
    const currentForm = event.target ? event.target.closest("form") : null;
    const isMobile = currentForm && currentForm.id === "FacetFiltersFormMobile";
    
    // Preserve URL parameters that should be maintained (e.g., q and type for search pages)
    const currentUrlParams = new URLSearchParams(window.location.search);
    const paramsToPreserve = ['q', 'type'];
    
    const forms = [];
    sortFilterForms.forEach((form) => {
      if (!isMobile) {
        if (form.id === "FacetFiltersForm" || form.id === "FacetFiltersFormSidebar") {
          forms.push(this.createSearchParams(form));
        }
      } else if (form.id === "FacetFiltersFormMobile") {
        forms.push(this.createSearchParams(form));
      }
    });
    
    // Merge form params and preserved URL params
    const mergedParams = new URLSearchParams(forms.join("&"));
    paramsToPreserve.forEach(param => {
      if (currentUrlParams.has(param) && !mergedParams.has(param)) {
        mergedParams.set(param, currentUrlParams.get(param));
      }
    });
    
    this.onSubmitForm(mergedParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1
          );
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) => {
      element.addEventListener("blur", this.onInputBlur.bind(this));
      element.addEventListener("keydown", this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
    
    const form = this.closest("form");
    if (form) {
      form.addEventListener("submit", (e) => {
        this.querySelectorAll("input.price-range__input").forEach((input) => {
          this.adjustToValidValues(input);
        });
        this.setMinAndMaxValues();
        this.convertToCents();
      });
    }
  }

  onInputBlur(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern =
      /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape|Home|End/;
    if (!event.key.match(pattern)) event.preventDefault();
    
    if (event.key === '.' && event.currentTarget.value.includes('.')) {
      event.preventDefault();
    }
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll("input.price-range__input");
    if (inputs.length < 2) return;
    
    const minInput = inputs[0];
    const maxInput = inputs[1];
    
    const rangeMax = maxInput.getAttribute("data-range-max") || 
                     (maxInput.placeholder ? parseFloat(maxInput.placeholder) : null);
    if (rangeMax) {
      minInput.setAttribute("data-range-max", rangeMax);
      maxInput.setAttribute("data-range-max", rangeMax);
    }
    
    if (maxInput.value && maxInput.value.trim() !== '') {
      const maxValue = parseFloat(maxInput.value);
      if (!isNaN(maxValue)) {
        minInput.setAttribute("data-max", maxValue.toString());
      }
    }
    if (minInput.value && minInput.value.trim() !== '') {
      const minValue = parseFloat(minInput.value);
      if (!isNaN(minValue)) {
        maxInput.setAttribute("data-min", minValue.toString());
      }
    }
    if (minInput.value === "" || minInput.value.trim() === "") {
      maxInput.setAttribute("data-min", "0");
    }
    if (maxInput.value === "" || maxInput.value.trim() === "") {
      const storedMax = minInput.getAttribute("data-range-max") || minInput.getAttribute("data-max");
      if (storedMax) {
        minInput.setAttribute("data-max", storedMax);
      }
    }
  }

  adjustToValidValues(input) {
    if (!input.value || input.value.trim() === '') return;
    
    const value = parseFloat(input.value);
    if (isNaN(value)) {
      input.value = '';
      return;
    }
    
    const min = parseFloat(input.getAttribute("data-min") || 0);
    const maxAttr = input.getAttribute("data-max");
    const max = maxAttr ? parseFloat(maxAttr) : Infinity;
    const rangeMax = parseFloat(input.getAttribute("data-range-max") || Infinity);

    const actualMax = rangeMax !== Infinity ? rangeMax : max;

    if (value < min) {
      input.value = min.toString();
    } else if (value > actualMax && actualMax !== Infinity) {
      input.value = actualMax.toString();
    }
  }
  
  convertToCents() {
    const inputs = this.querySelectorAll("input.price-range__input");
    inputs.forEach((input) => {
      if (input.value && input.value.trim() !== '') {
        input.setAttribute("data-original-value", input.value);
        const numericValue = parseFloat(input.value);
        if (!isNaN(numericValue)) {
          input.value = Math.round(numericValue * 100);
        }
      }
    });
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    if (facetLink) {
      facetLink.setAttribute("role", "button");
      facetLink.addEventListener("click", this.closeFilter.bind(this));
      facetLink.addEventListener("keyup", (event) => {
        event.preventDefault();
        if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
      });
    }
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");
    if (form) form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);

// Handle clear all filters button
function initializeClearButtons() {
  document.querySelectorAll('a.facets-clear-btn').forEach(clearBtn => {
    // Skip if already initialized
    if (clearBtn.dataset.initialized === 'true') return;
    clearBtn.dataset.initialized = 'true';
    
    clearBtn.setAttribute("role", "button");
    clearBtn.addEventListener("click", function(event) {
      event.preventDefault();
      
      // Get current URL parameters to preserve search query and type
      const currentUrlParams = new URLSearchParams(window.location.search);
      const paramsToPreserve = ['q', 'type'];
      const preservedParams = new URLSearchParams();
      
      paramsToPreserve.forEach(param => {
        if (currentUrlParams.has(param)) {
          preservedParams.set(param, currentUrlParams.get(param));
        }
      });
      
      // Use FacetFiltersForm to render the page with cleared filters
      // This will clear all filters while preserving search parameters
      FacetFiltersForm.renderPage(preservedParams.toString(), event);
    });
    
    clearBtn.addEventListener("keyup", function(event) {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") {
        clearBtn.click();
      }
    });
  });
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', initializeClearButtons);

// Re-initialize after dynamic updates
const originalRenderFilters = FacetFiltersForm.renderFilters;
FacetFiltersForm.renderFilters = function(html, event) {
  originalRenderFilters.call(this, html, event);
  // Re-initialize clear buttons after filters are rendered
  setTimeout(initializeClearButtons, 0);
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.eventsBound = false;
    this.bindEvents();
  }

  bindEvents() {
    if (this.eventsBound) {
      const closeButtons = this.querySelectorAll('.mobile-facets-close-button');
      closeButtons.forEach((closeButton) => {
        if (!closeButton.hasAttribute('data-listener-bound')) {
          closeButton.addEventListener('click', (e) => {
            e.preventDefault();
            this.closeDrawer();
          });
          closeButton.setAttribute('data-listener-bound', 'true');
        }
      });
      return;
    }
    
    this.eventsBound = true;

    const openButton = this.querySelector('.mobile-facets-open-button');
    if (openButton) {
      openButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.openDrawer();
      });
    }

    const closeButtons = this.querySelectorAll('.mobile-facets-close-button');
    closeButtons.forEach((closeButton) => {
      closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeDrawer();
      });
      closeButton.setAttribute('data-listener-bound', 'true');
    });

    const overlay = this.querySelector('.mobile-facets-overlay');
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.closeDrawer();
        }
      });
    }

    const drawer = this.querySelector('.mobile-facets-drawer');
    if (drawer) {
      drawer.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }

    this.escapeHandler = (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeDrawer();
      }
    };
    document.addEventListener('keydown', this.escapeHandler);
  }

  openDrawer() {
    if (this.isOpen) return;
    
    const overlay = this.querySelector('.mobile-facets-overlay');
    const drawer = this.querySelector('.mobile-facets-drawer');
    
    if (overlay && drawer) {
      this.isOpen = true;
      document.body.style.overflow = 'hidden';
      
      drawer.style.visibility = 'visible';
      
      overlay.classList.remove('pointer-events-none');
      overlay.classList.remove('opacity-0');
      overlay.classList.add('opacity-100');
      
      requestAnimationFrame(() => {
        drawer.classList.remove('translate-x-full');
      });
    }
  }

  closeDrawer() {
    if (!this.isOpen) return;
    
    const overlay = this.querySelector('.mobile-facets-overlay');
    const drawer = this.querySelector('.mobile-facets-drawer');
    
    if (overlay && drawer) {
      drawer.classList.add('translate-x-full');
      overlay.classList.remove('opacity-100');
      overlay.classList.add('opacity-0');
      
      setTimeout(() => {
        drawer.style.visibility = 'hidden';
        overlay.classList.add('pointer-events-none');
        this.isOpen = false;
        document.body.style.overflow = '';
      }, 300);
    }
  }
}

customElements.define("menu-drawer", MenuDrawer);
